/* eslint-disable import/no-deprecated -- handle legacy route redirects */
import { useEffect, useMemo } from 'react';

import { ApolloError } from '@apollo/client';
import { generatePath, useLocation, useSearchParams } from '@remix-run/react';

import { Observability, useObservability } from 'platform/observability';
import { useNavigate } from 'platform/remix-run/react';

import { HUB_BASE_ROUTES } from 'shared/helpers/routes';

import { useAuth } from './auth-fe';
import { parseAddressRoute } from './routingHelpers';

export interface IRedirectToLatestLeadProps {
  loading: boolean;
  error?: ApolloError;
  addressUUID?: string | null;
  isBuyOnlyCustomer: boolean;
}

export const useGetLatestLeadUrl = (
  {
    loading,
    error,
    addressUUID,
    isBuyOnlyCustomer,
    pathname,
    search,
  }: IRedirectToLatestLeadProps & { pathname: string; search: URLSearchParams },
  trackError: Observability['trackError'],
): string | undefined =>
  useMemo(() => {
    const { basePath, addressUUID: addressUUIDParam, subPath } = parseAddressRoute(pathname);

    if (loading || error) {
      return;
    }

    /** Does nothing for buy only customers */
    if (isBuyOnlyCustomer) {
      return;
    }

    /** Does nothing + tracks if route is already in correct base route format */
    if (addressUUIDParam) {
      return;
    }

    /** Appends addressUUID to the base route if customer has a sell direct lead */
    if (HUB_BASE_ROUTES.includes(pathname) && addressUUID) {
      // NOTE: query param should be calculated as close to the url generation as possible. This is to ensure that any additions to the query params are included in the redirect.
      const queryParams = search.toString().trim().length > 0 ? `?${search.toString()}` : '';
      const leadUrl = `${basePath}/${addressUUID}${subPath}/${queryParams}`;
      return generatePath(leadUrl, { addressUUID });
    }

    trackError('missing-address-uuid', new Error('Missing address UUID'));
  }, [loading, error, isBuyOnlyCustomer, pathname, addressUUID, trackError, search]);

/**
 * Handles redirecting to the latest lead when no addressUUID is present on the URL and the customer has one associated with them
 *
 * -----------------------------------------------------------------------------
 * Tested by the integration tests in `cypress/integration/hub/routing/`
 */
export const useRedirectToLatestLead = (props: IRedirectToLatestLeadProps): void => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [search] = useSearchParams();
  const { trackError } = useObservability();

  const leadUrl = useGetLatestLeadUrl({ ...props, pathname, search }, trackError);
  useEffect(() => {
    /** Appends addressUUID to the base route if customer has a sell direct lead */
    leadUrl && navigate(leadUrl, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run this effect when the url changes
  }, [leadUrl]);
};

export const maybeAppendCustomerUUIDOverride = (
  searchParams: URLSearchParams,
  auth: ReturnType<typeof useAuth>,
  customerUUIDOverride?: string,
): string => {
  // Append customer UUID to search params if it's different from the auth'd customer AND auth'd customer is an Opendoor employee
  if (
    auth.state === 'authenticated' &&
    customerUUIDOverride &&
    // Only allow customer UUID overrides for customers with Opendoor internal access
    auth.user?.hasOpendoorInternalAccess &&
    // Only require customerUUID override when the lead customer is different from the auth'd customer
    customerUUIDOverride !== auth.user.customerUuid
  ) {
    searchParams.set('customer', customerUUIDOverride);
  }

  // NOTE: query param should be calculated as close to the url generation as possible. This is to ensure that any additions to the query params are included in the redirect.
  return searchParams.toString().trim().length > 0 ? `?${searchParams.toString()}` : '';
};
